import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { MapPin, List, Percent, Navigation, Loader2, UtensilsCrossed, ShoppingBag, Sparkles } from "lucide-react";
import "leaflet/dist/leaflet.css";
import Logo from "../components/Logo";

// Fix for default marker icons in leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom gold marker icon for businesses
const goldIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z" fill="#D4AF37"/>
      <circle cx="12" cy="12" r="6" fill="#020617"/>
    </svg>
  `),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36]
});

// User location icon (blue)
const userIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
      <circle cx="12" cy="12" r="10" fill="#3B82F6" stroke="#fff" stroke-width="3"/>
      <circle cx="12" cy="12" r="4" fill="#fff"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

const categories = [
  { id: "all", name: "All", icon: Sparkles },
  { id: "dining", name: "Dining", icon: UtensilsCrossed },
  { id: "bar", name: "Bars", icon: UtensilsCrossed },
  { id: "shopping", name: "Shopping", icon: ShoppingBag },
  { id: "services", name: "Services", icon: Sparkles },
  { id: "beauty", name: "Beauty", icon: Sparkles },
  { id: "activities", name: "Activities", icon: Sparkles },
];

// Component to recenter map
const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 14, { duration: 1.5 });
    }
  }, [position, map]);
  return null;
};

const BusinessMapPage = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [subLocations, setSubLocations] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  
  // User location state
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [flyToUser, setFlyToUser] = useState(null);

  // Map center based on territory
  const territory = localStorage.getItem("advanta_territory") || "costa_del_sol";
  const territoryCenters = {
    costa_del_sol: [36.5, -4.7],
    tenerife: [28.1, -16.6],
    majorca: [39.6, 2.9],
  };
  const mapCenter = territoryCenters[territory] || [36.5, -4.7];
  const mapZoom = 10;

  useEffect(() => {
    fetchSubLocations();
    fetchBusinesses();
  }, []);

  const fetchSubLocations = async () => {
    try {
      const response = await axios.get(`${API}/sub-locations`);
      setSubLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch sub-locations:", error);
    }
  };

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/businesses`);
      setBusinesses(response.data);
    } catch (error) {
      console.error("Failed to fetch businesses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude, accuracy });
        setFlyToUser([latitude, longitude]);
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError("Location permission denied. Please enable location access.");
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError("Location information unavailable.");
            break;
          case error.TIMEOUT:
            setLocationError("Location request timed out.");
            break;
          default:
            setLocationError("Unable to get your location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const filteredBusinesses = businesses.filter(business => {
    const matchesCategory = selectedCategory === "all" || business.category === selectedCategory;
    const matchesLocation = selectedLocation === "all" || business.sub_location === selectedLocation;
    const hasCoordinates = business.latitude && business.longitude;
    return matchesCategory && matchesLocation && hasCoordinates;
  }).map(business => {
    // Add distance if user location is available
    if (userLocation && business.latitude && business.longitude) {
      return {
        ...business,
        distance: calculateDistance(
          userLocation.lat, userLocation.lng,
          business.latitude, business.longitude
        )
      };
    }
    return business;
  }).sort((a, b) => {
    // Sort by distance if available
    if (a.distance !== undefined && b.distance !== undefined) {
      return a.distance - b.distance;
    }
    return 0;
  });

  const getCategoryIcon = (category) => {
    switch (category) {
      case "dining": return <UtensilsCrossed className="w-4 h-4" />;
      case "shopping": return <ShoppingBag className="w-4 h-4" />;
      default: return <Sparkles className="w-4 h-4" />;
    }
  };

  const formatDistance = (km) => {
    if (km < 1) return `${Math.round(km * 1000)}m`;
    return `${km.toFixed(1)}km`;
  };

  return (
    <div className="min-h-screen bg-[#020617] flex flex-col">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <Link to="/businesses">
              <Button variant="ghost" className="text-white hover:text-[#D4AF37]">
                <List className="w-4 h-4 mr-2" />
                List View
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Filters */}
      <div className="bg-[#0B0F19] border-b border-[#D4AF37]/10 px-6 py-3 z-40">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-4">
          {/* Categories */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === cat.id
                    ? "bg-[#D4AF37] text-[#020617]"
                    : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </button>
            ))}
          </div>

          {/* Location Filter */}
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
              <MapPin className="w-4 h-4 mr-2 text-[#D4AF37]" />
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {subLocations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* My Location Button */}
          <Button
            onClick={handleGetUserLocation}
            disabled={locationLoading}
            className={`${userLocation ? 'bg-blue-500 hover:bg-blue-600' : 'bg-white/5 hover:bg-white/10'} text-white border-0`}
            data-testid="my-location-btn"
          >
            {locationLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Navigation className="w-4 h-4 mr-2" />
            )}
            {userLocation ? 'You are here' : 'Find Me'}
          </Button>

          <span className="text-white/50 text-sm">
            {filteredBusinesses.length} partners on map
            {userLocation && filteredBusinesses.length > 0 && filteredBusinesses[0].distance !== undefined && (
              <span className="ml-2 text-blue-400">
                • Nearest: {formatDistance(filteredBusinesses[0].distance)}
              </span>
            )}
          </span>
        </div>
        
        {/* Location Error */}
        {locationError && (
          <div className="max-w-6xl mx-auto mt-2">
            <p className="text-red-400 text-sm">{locationError}</p>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="flex-1 relative" style={{ minHeight: "calc(100vh - 180px)" }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#020617]">
            <div className="text-white">Loading map...</div>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: "100%", width: "100%", minHeight: "500px" }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {/* Recenter map when user location is found */}
            {flyToUser && <RecenterMap position={flyToUser} />}
            
            {/* User Location Marker */}
            {userLocation && (
              <>
                <Marker
                  position={[userLocation.lat, userLocation.lng]}
                  icon={userIcon}
                >
                  <Popup>
                    <div className="text-center">
                      <p className="font-semibold">You are here</p>
                      <p className="text-xs text-gray-500">
                        Accuracy: ~{Math.round(userLocation.accuracy)}m
                      </p>
                    </div>
                  </Popup>
                </Marker>
                {/* Accuracy circle */}
                <Circle
                  center={[userLocation.lat, userLocation.lng]}
                  radius={userLocation.accuracy}
                  pathOptions={{
                    color: '#3B82F6',
                    fillColor: '#3B82F6',
                    fillOpacity: 0.1,
                    weight: 1
                  }}
                />
              </>
            )}
            
            {/* Business Markers */}
            {filteredBusinesses.map((business) => (
              <Marker
                key={business.id}
                position={[business.latitude, business.longitude]}
                icon={goldIcon}
                eventHandlers={{
                  click: () => setSelectedBusiness(business)
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-semibold text-base mb-1">{business.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      {getCategoryIcon(business.category)}
                      <span className="capitalize">{business.category}</span>
                      <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium">
                        {business.discount_level}% off
                      </span>
                    </div>
                    {business.distance !== undefined && (
                      <p className="text-xs text-blue-600 mb-2 font-medium">
                        {formatDistance(business.distance)} from you
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{business.description}</p>
                    <p className="text-xs text-gray-500 mb-3">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {business.address}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/businesses/${business.id}`)}
                        className="flex-1 bg-[#D4AF37] text-[#020617] py-2 rounded text-sm font-medium hover:bg-[#F5DEB3] transition-colors"
                      >
                        View Details
                      </button>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-blue-500 text-white rounded text-sm font-medium hover:bg-blue-600 transition-colors flex items-center"
                        title="Get Directions"
                      >
                        <Navigation className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}

        {/* Business Info Panel (Mobile) */}
        {selectedBusiness && (
          <div className="absolute bottom-0 left-0 right-0 bg-[#0B0F19] border-t border-[#D4AF37]/20 p-4 md:hidden z-50">
            <button 
              onClick={() => setSelectedBusiness(null)}
              className="absolute top-2 right-2 text-white/50 hover:text-white"
            >
              ✕
            </button>
            <h3 className="font-['Playfair_Display'] text-lg text-white mb-1">{selectedBusiness.name}</h3>
            <div className="flex items-center gap-2 text-sm text-white/60 mb-2">
              {getCategoryIcon(selectedBusiness.category)}
              <span className="capitalize">{selectedBusiness.category}</span>
              <span className="bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded text-xs font-medium">
                {selectedBusiness.discount_level}% off
              </span>
              {selectedBusiness.distance !== undefined && (
                <span className="text-blue-400 text-xs">
                  {formatDistance(selectedBusiness.distance)} away
                </span>
              )}
            </div>
            <p className="text-sm text-white/50 mb-3">{selectedBusiness.address}</p>
            <Button 
              onClick={() => navigate(`/businesses/${selectedBusiness.id}`)}
              className="btn-gold w-full py-3 rounded"
            >
              View Details
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessMapPage;
